// 카탈로그 관리 화면(WBS 1.2)의 표시·검증 유틸. 순수 함수만 둔다 — 화면 없이 테스트된다.
//
// 금액 단위는 전부 원(KRW) 정수다. 소수점이 없다.
//
// 여기 있는 검증은 `handoff/pricing-v1/api.md` 의 서버 검증과 **같은 규칙**이다.
// 화면이 더 느슨하면 서버가 400 을 내고, 더 빡빡하면 서버가 받는 값을 화면이 막는다.
// 둘 다 운영자에게는 "왜 안 되는지 모르겠는" 상태라 규칙을 어긋나게 두지 않는다.

import type { AdminCatalogProduct, Cta, PriceStatus, Surcharge, UpdateEnglishRequest, UpdateSurchargeRequest } from '@/lib/api/catalog';
// 금액 표기를 새로 만들지 않는다. 같은 어드민 안에서 "400,000원"과 "₩400,000"이 섞이면
// 같은 값이 다른 값처럼 읽힌다.
import { formatWon } from '@/utils/membership';

// ── 라벨 ─────────────────────────────────────────────

/**
 * 표 안의 배지용 짧은 라벨.
 *
 * `pricing.core.ts` 의 `PRICE_STATUS_BADGE` 는 "예시 단가 · 회의에서 확정 예정"처럼 길다.
 * 그쪽은 고객 화면(Track C)에서 한 상품을 크게 보여줄 때 쓰는 문장이고, 54행짜리 표에는
 * 열 하나를 다 잡아먹어 못 쓴다. 뜻은 같고 길이만 다르다.
 */
export const PRICE_STATUS_LABEL: Record<PriceStatus, string> = {
  DRAFT: '예시 단가',
  CONFIRMED: '가격 확정',
};

/** 고객 화면이 그릴 버튼. 어드민에서는 "이 상품이 지금 고객에게 어떻게 보이는가"를 알려준다. */
export const CTA_LABEL: Record<Cta, string> = {
  ORDER: '바로 결제',
  INQUIRY: '상담 신청',
  // 고객 화면(`lib/catalog/format` 의 CTA_LABEL)과 같은 낱말이어야 한다 — 2026-09-05 부터
  // 접수도 「상담 신청」이다. 어드민이 「고객에게 어떻게 보이는가」를 보는 자리라 갈리면 거짓이 된다.
  INTAKE: '상담 신청',
};

/** 서버가 403 을 낼 때 쓰는 문구. 서버가 사유를 안 실어 보내면 이 값을 그대로 띄운다. */
export const FORBIDDEN_MESSAGE = '가격 변경 권한이 없습니다.';

// ── 표시 ─────────────────────────────────────────────

/**
 * 할증 설정 한 줄 요약.
 *
 * `api.md` 의 이력 표기와 **같은 문장**이다. 이력에 남는 글과 화면에 뜨는 글이 다르면
 * 운영자가 "내가 바꾼 게 이게 맞나"를 대조할 수 없다.
 * 예: `장 · 10장 포함 · 초과분 30,000원/장 · 30장부터 상담·견적`
 */
export function surchargeSummary(surcharge: Surcharge): string {
  if (!surcharge.enabled) return '사용 안 함';

  const unit = surcharge.unitLabel;
  const parts = [unit, `${surcharge.included}${unit} 포함`, `초과분 ${formatWon(surcharge.unitAmount)}/${unit}`];
  if (surcharge.quoteFrom > 0) parts.push(`${surcharge.quoteFrom}${unit}부터 상담·견적`);

  return parts.join(' · ');
}

// `2026-08-06T14:22:10` / `2026-08-06 14:22:10` 둘 다 받는다.
const TIMESTAMP_PATTERN = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/;

/**
 * 이력 시각 표기. `2026-08-06T14:22:10` → `2026-08-06 14:22`
 *
 * `new Date()` 를 쓰지 않는다. 서버가 주는 값에는 타임존이 없어서 브라우저가 **로컬 시각으로**
 * 해석하는데, 서버는 KST 로 적어 보낸다. 해외에서 접속한 운영자에게는 변경 시각이 몇 시간씩
 * 밀려 보인다. 문자열을 그대로 자르면 어디서 보든 서버가 적은 시각이 그대로 뜬다.
 */
export function formatChangedAt(changedAt: string): string {
  const matched = TIMESTAMP_PATTERN.exec(changedAt);
  if (!matched) return changedAt;

  const [, year, month, day, hour, minute] = matched;
  return `${year}-${month}-${day} ${hour}:${minute}`;
}

/** 입력칸 표시용 — 숫자만 남기고 3자리마다 쉼표. 빈 값은 빈 값으로 둔다(0 으로 만들지 않는다). */
export function formatAmountInput(raw: string): string {
  const digits = raw.replace(/[^\d]/g, '');
  if (!digits) return '';
  return Number(digits).toLocaleString('ko-KR');
}

// ── 검증 ─────────────────────────────────────────────

export type ParseResult<T> = { ok: true; value: T } | { ok: false; error: string };

const ok = <T>(value: T): ParseResult<T> => ({ ok: true, value });
const fail = <T>(error: string): ParseResult<T> => ({ ok: false, error });

/** 쉼표·공백을 걷어낸 정수. 숫자가 아니거나 음수면 null. */
function toInteger(raw: string): number | null {
  const cleaned = raw.replace(/[,\s]/g, '');
  if (!/^\d+$/.test(cleaned)) return null;
  return Number(cleaned);
}

/** 기본료 — 0 보다 큰 정수 (api.md `PUT .../pricing`). */
export function parseBaseAmount(raw: string): ParseResult<number> {
  const value = toInteger(raw);
  if (value === null) return fail('기본료는 숫자로 입력해 주세요.');
  if (value <= 0) return fail('기본료는 0보다 커야 합니다.');
  return ok(value);
}

export const REVISION_LIMIT_MAX = 20;

/**
 * 포함 수정 횟수 — 0 이상 20 이하 정수 (api.md `PUT .../revision-limit`).
 * 20 을 넘으면 사실상 무제한이라 횟수를 두는 의미가 없다.
 */
export function parseRevisionLimit(raw: string): ParseResult<number> {
  const value = toInteger(raw);
  if (value === null) return fail('포함 수정 횟수는 숫자로 입력해 주세요.');
  if (value > REVISION_LIMIT_MAX) return fail(`포함 수정 횟수는 ${REVISION_LIMIT_MAX}회 이하로 입력해 주세요.`);
  return ok(value);
}

// ── 한 줄 설명 ───────────────────────────────────────

/**
 * 한 줄 설명의 길이 상한.
 *
 * **이 숫자는 화면이 정한 것이 아니라 컬럼이 정한 것이다.** `product.description` 이
 * VARCHAR(255) 라 더 긴 값은 저장 단계에서 데이터 잘림으로 죽는다. 변경 이력의
 * `before_text`·`after_text` 도 같은 255 인데 그쪽은 서버가 상한 안으로 잘라 주므로,
 * 이 상한이 지키는 것은 상품 행이다.
 * 서버도 `@Size(max = 255)` 로 같은 값을 막는다. 한쪽만 고치지 말 것.
 */
export const DESCRIPTION_MAX = 255;

/**
 * 보이지 않는 글자 — 서식문자(유니코드 Cf)와 공백이 아닌 ASCII 제어문자.
 *
 * Cf 에는 폭 없는 공백(U+200B)·soft hyphen(U+00AD)·양방향 재정의(U+200E) 처럼 **화면에는
 * 안 보이면서 문장을 다르게 읽히게 만드는** 글자가 들어 있다. 뒤의 범위는 자바의
 * `[\p{Cntrl}&&[^\t\n\x0B\f\r]]` 와 같은 집합이다(공백류만 뺀 `\x00-\x1F` 와 `\x7F`) —
 * 그쪽은 다음 단계에서 공백으로 접히므로 여기서 지우면 안 된다.
 *
 * ## `\p{Cf}` 를 쓰지 않고 코드포인트를 펼쳐 적은 이유
 *
 * 유니코드 속성 이스케이프에는 `u` 플래그가 필수인데, **이 모듈은 공개 페이지 번들에
 * 들어간다** — `providers.tsx` → `ChatWidget` → … → `RecommendCard` 가 이 파일의
 * `PRICE_STATUS_LABEL` 을 정적으로 가져온다. 모듈 최상단의 정규식은 모듈이 평가되는 순간
 * 만들어지는데, `\p{...}` 를 모르는 엔진(Firefox 78 미만 — 이 저장소에 browserslist 설정이
 * 없어 적용되는 Next 기본 지원 범위에 실제로 들어 있다)에서는 그 자리에서 SyntaxError 가 나고
 * **루트 청크가 죽어 공개 페이지 전체가 빈 화면**이 된다. 어드민 입력칸 하나 때문에 그럴 수 없다.
 *
 * 펼친 목록은 **BMP 안의 Cf 전부**다. BMP 밖(U+110BD·U+1D173~U+1D17A·U+E0001 등)은 뺐다 —
 * `u` 없는 정규식이 서로게이트 반쪽을 건드리는 편이 더 위험하고, 붙여넣기에 섞이는 글자가
 * 아니다. 서버(`foldToOneLine`)는 자바 `\p{Cf}` 라 그 글자들까지 지우므로 그 예외적인
 * 입력에서만 두 쪽 결과가 갈린다.
 */
// `no-control-regex` 는 "실수로 섞여 든 제어문자"를 잡으려는 규칙이다. 여기서는 제어문자를
// 지우는 것이 이 정규식의 일이라 일부러 적었다.
// eslint-disable-next-line no-control-regex
const INVISIBLE_PATTERN = /[\u00AD\u0600-\u0605\u061C\u06DD\u070F\u08E2\u180E\u200B-\u200F\u202A-\u202E\u2060-\u2064\u2066-\u206F\uFEFF\uFFF9-\uFFFB]|[\u0000-\u0008\u000E-\u001F\u007F]/g;

/**
 * 공백류. `\s` 가 아니라 유니코드 White_Space 집합을 그대로 펼쳐 적는다 — 자바의
 * `\p{IsWhite_Space}` 와 같은 집합이라야 U+0085(NEL)·U+2028 같은 글자에서 양쪽 결과가
 * 갈리지 않는다. 이 집합은 전부 BMP 안이라 서버와 **완전히** 같다.
 * 속성 이스케이프를 안 쓰는 이유는 위와 같다.
 */
// 적어야 하므로 의도한 것이다.
// 탭과 개행도 제어문자라 같은 규칙이 걸린다. 공백류를 접는 것이 이 정규식의 일이라
// 그 글자들을 적는 것은 의도한 것이다.
// eslint-disable-next-line no-control-regex
const WHITESPACE_PATTERN = /[\u0009-\u000D\u0020\u0085\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]+/g;

/**
 * 한 줄 설명을 "한 줄"로 만든다 — 보이지 않는 글자를 지우고, 줄바꿈과 연속 공백을
 * 한 칸으로 접고, 앞뒤를 턴다.
 *
 * 멋 부리는 정리가 아니라 **프롬프트 위조를 입구에서 막는 것**이다. 챗봇 프롬프트의
 * 상품 목록은 `- CODE (CAT) 상품명 — 설명` 으로 **한 줄 = 한 상품**이다. 설명에
 * 줄바꿈이 섞이면 `\n- FAKE-01 (CONT) 무료 상담` 같은 줄이 없는 상품처럼 보인다.
 * 서버도 같은 일을 하지만(프롬프트 조립 시 한 줄로 접는다), 저장되는 값 자체가
 * 한 줄이면 어드민 목록에서도 설명이 한 줄로 읽힌다.
 *
 * ## 이 함수의 짝은 서버다 — 규칙이 갈리면 저장이 영영 「저장 안 됨」으로 보인다
 *
 * 짝: `lawkit-api` 의
 * `app/src/main/java/legalcare/lawkit/api/domain/common/service/CatalogCommandService.java`
 * → `foldToOneLine()`. **한쪽만 고치지 말 것.**
 *
 * 서버는 `\p{Cf}` 를 **지운 뒤** 공백을 접는다. 화면이 JS 의 `\s` 로만 접으면
 * U+200B·U+00AD·U+200E 가 그대로 남는다(`\s` 에는 그 글자들이 없다). 그러면 워드·웹에서
 * 복사해 붙여넣은 문장 — 폭 없는 공백이 섞이는 아주 흔한 경우 — 을 저장할 때
 * **서버가 글자를 뗀 값을 저장·반환**하고, 화면이 들고 있는 값과 달라
 * `DescriptionSection` 의 `isDirty` 가 내려가지 않는다. 운영자에게는 저장을 눌러도
 * 계속 「저장 안 됨」으로 보이는 절이 된다. 지우는 순서(지우기 → 접기)도 서버와 같아야
 * 한다 — 먼저 접으면 「검토\n무료」가 「검토무료」로 붙는다.
 */
export function normalizeDescription(raw: string): string {
  // 접은 뒤 남는 공백은 U+0020 뿐이라, 자바 `trim()`(U+0020 이하만 턴다)과 결과가 같다.
  return raw.replace(INVISIBLE_PATTERN, '').replace(WHITESPACE_PATTERN, ' ').trim();
}

/**
 * 한 줄 설명 (`PUT .../description`).
 *
 * **빈 값을 막지 않는다.** 54종을 다 채우지 않는 것이 전제이고, 잘못 적은 설명을
 * 지우는 것도 정상 조작이다. 비면 `null` 을 돌려준다 — 빈 문자열을 저장하면
 * "설명이 있는데 빈 설명"이 되어 프롬프트에 빈 구분자(` — `)가 붙는다.
 *
 * 금액이 적혀 있는지는 보지 않는다. 화면 검증이 서버보다 빡빡하면 서버가 받는 값을
 * 화면이 막는 상태가 되고, 운영자는 왜 막혔는지 알 수 없다. 대신 입력칸 안내로
 * "금액은 적지 않는다"를 알린다 — 프롬프트는 금액을 일부러 싣지 않는 설계다.
 */
export function parseDescription(raw: string): ParseResult<string | null> {
  const text = normalizeDescription(raw);
  if (text.length > DESCRIPTION_MAX) return fail(`한 줄 설명은 ${DESCRIPTION_MAX}자 이하로 입력해 주세요. (현재 ${text.length}자)`);
  return ok(text || null);
}

/** 폼이 들고 있는 값. 수량 칸은 지우는 도중에도 유지돼야 해서 전부 문자열이다. */
export interface SurchargeDraft {
  enabled: boolean;
  unitLabel: string;
  /** 영문 단위. **단수형으로 넣는다** (page·issue·item·hour) — 복수는 화면이 만든다 */
  unitLabelEn: string;
  included: string;
  unitAmount: string;
  quoteFrom: string;
}

/**
 * 영문 단위는 `surcharge` 가 아니라 상품에서 온다.
 *
 * `product.english.surcharge.unitLabel` 을 쓰면 안 된다 — 영문이 비면 국문 단위가
 * 담기므로, 그 값이 입력칸에 들어가면 `장` 이 영문 단위로 저장된다.
 */
export function toSurchargeDraft(surcharge: Surcharge, unitLabelEn: string | null): SurchargeDraft {
  return {
    enabled: surcharge.enabled,
    unitLabel: surcharge.unitLabel,
    unitLabelEn: unitLabelEn ?? '',
    included: String(surcharge.included),
    unitAmount: String(surcharge.unitAmount),
    quoteFrom: String(surcharge.quoteFrom),
  };
}

/**
 * 수량 할증 (api.md `PUT .../surcharge`).
 *
 * `enabled` 가 꺼져 있어도 나머지 칸을 검증한다. 페이로드에는 어차피 전부 실려 가고,
 * 빈 단위 이름으로 저장해 두면 나중에 켜는 순간 서버가 400 을 낸다 — 켠 사람은
 * 자기가 건드리지도 않은 칸 때문에 막힌 이유를 알 수 없다.
 */
export function parseSurchargeDraft(draft: SurchargeDraft, visibleEn: boolean): ParseResult<UpdateSurchargeRequest> {
  const unitLabel = draft.unitLabel.trim();
  if (!unitLabel) return fail('단위 이름을 입력해 주세요.');

  const included = toInteger(draft.included);
  if (included === null) return fail('포함 수량은 0 이상 숫자로 입력해 주세요.');

  const unitAmount = toInteger(draft.unitAmount);
  if (unitAmount === null) return fail('단위 단가는 0 이상 숫자로 입력해 주세요.');

  const quoteFrom = toInteger(draft.quoteFrom);
  if (quoteFrom === null) return fail('상담 전환 수량은 0 이상 숫자로 입력해 주세요.');

  // 상한이 포함 수량 이하면 "기본 수량인데도 결제가 안 되는" 상품이 된다.
  if (quoteFrom > 0 && quoteFrom <= included) return fail('상담 전환 수량은 포함 수량보다 커야 합니다. (0 = 상한 없음)');

  // 영문에 나가는 상품이 할증을 켜면 영문 단위가 필수다. 서버도 400 을 내지만,
  // 저장을 눌러 왕복한 뒤 거절당하는 것보다 여기서 이유가 보이는 편이 낫다.
  const unitLabelEn = draft.unitLabelEn.trim();
  if (draft.enabled && visibleEn && !unitLabelEn) return fail(SURCHARGE_UNIT_EN_REQUIRED);

  return ok({ enabled: draft.enabled, unitLabel, unitLabelEn: unitLabelEn || null, included, unitAmount, quoteFrom });
}

// ── 영문 서비스 ───────────────────────────────────────
//
// **영문 가격을 여기서 계산하지 않는다.** 배율은 서버 `pricing_setting` 에 있어 배포 없이
// 바뀌므로, 프론트에 숫자를 두면 운영자가 배율을 조정하는 날 화면과 청구액이 갈린다.
// 화면에 그리는 영문가는 전부 응답의 `product.english` 를 그대로 쓴 것이다.

/**
 * 폼이 들고 있는 값. 지우는 도중 상태를 유지해야 해서 전부 문자열이다.
 *
 * `deliverables` 를 줄 단위로 받는다. 배열 편집기를 만들지 않은 것은 항목이 한
 * 상품에 한둘이고, 줄바꿈이 그 편집을 가장 적은 조작으로 표현하기 때문이다.
 */
export interface EnglishDraft {
  nameEn: string;
  deliverables: string;
  visibleEn: boolean;
}

/** 서버 400 과 같은 사유. 같은 말을 써야 운영자가 화면 안내와 서버 거절을 같은 것으로 읽는다. */
export const ENGLISH_NAME_REQUIRED = '영문 페이지에 노출하려면 영문 상품명을 입력해 주세요.';
export const SURCHARGE_UNIT_EN_REQUIRED = '영문 페이지에 노출 중인 상품입니다. 할증 단위의 영문 표기를 입력해 주세요.';

/**
 * `next-code` 응답에서 입력칸에 넣을 코드를 꺼낸다. 못 꺼내면 `null`.
 *
 * **이 함수가 있는 이유.** 서버는 `ResultResponse<String>` 을 주므로 봉투 안이
 * 문자열인데, 프론트가 `{ code: string }` 으로 잡아 두고 `data.code` 를 읽었다.
 * `undefined` 가 상태로 들어갔고 다음 렌더의 `code.trim()` 이 던져 어드민 화면이
 * 통째로 죽었다 — 분류를 고르는 것만으로.
 *
 * 그래서 모양을 **믿지 않고 확인한다.** 상대가 다른 저장소의 자바 시그니처라
 * 타입 검사가 지켜 주지 못하는 경계다. 값이 이상하면 제안을 포기할 뿐 화면은
 * 살아 있어야 한다 — 코드는 어차피 운영자가 고칠 수 있다.
 */
export function suggestedCode(data: unknown): string | null {
  if (typeof data !== 'string') return null;
  const trimmed = data.trim();
  return trimmed === '' ? null : trimmed;
}

/** 줄 단위 입력을 배열로. 빈 줄은 버린다 — 서버도 같은 일을 한다. */
export function toDeliverableList(text: string): string[] {
  return text
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);
}

/**
 * `nameEn` 은 어드민 응답에 없을 수도 있다(계약상 `locale=en` 일 때 채워지는 필드다).
 * 그 경우 빈 칸으로 시작하는데, **저장하면 기존 영문명이 지워진다** — 페이로드가 전체 교체라
 * "그대로 두기"를 표현할 방법이 없다. 어드민 응답이 `nameEn` 을 싣는 것이 전제다.
 * `deliverablesEn` 도 같다.
 */
export function toEnglishDraft(product: Pick<AdminCatalogProduct, 'nameEn' | 'deliverablesEn' | 'visibleEn'>): EnglishDraft {
  return {
    nameEn: product.nameEn ?? '',
    deliverables: (product.deliverablesEn ?? []).join('\n'),
    visibleEn: product.visibleEn,
  };
}

/**
 * 영문 노출 설정 (`PUT .../english`).
 *
 * 노출을 켜면서 이름이 비면 막는다 — 서버도 400 을 내지만, 저장을 눌러 왕복한 뒤 거절당하는
 * 것보다 체크하는 순간 이유가 보이는 편이 낫다. 화면 차단은 안내이지 방어가 아니다.
 *
 * 받으실 것은 비어도 막지 않는다. 비면 영문 페이지에서 그 줄이 빠질 뿐 한글이 뜨지는
 * 않아서, 막기보다 운영자 판단에 둔다.
 */
export function parseEnglishDraft(draft: EnglishDraft): ParseResult<UpdateEnglishRequest> {
  const nameEn = draft.nameEn.trim();
  if (draft.visibleEn && !nameEn) return fail(ENGLISH_NAME_REQUIRED);

  const deliverablesEn = toDeliverableList(draft.deliverables);

  // 공백만 남은 이름은 null 로 보낸다. 빈 문자열을 저장하면 "이름이 있는데 빈 이름"이 된다.
  return ok({
    nameEn: nameEn || null,
    deliverablesEn: deliverablesEn.length > 0 ? deliverablesEn : null,
    visibleEn: draft.visibleEn,
  });
}
